import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Inquiry, InquiryStatus, ClientFile } from "@visitproof/types";
import { InquiryService } from "../services/InquiryService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

// Re-type for backward compatibility
type File = ClientFile;

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface InquiryContextProps {
  loading: Loadings;
  error: Errors;
  status: InquiryStatus;
  setStatus: (status: InquiryStatus) => void;
  inquiries: Inquiry[];
  selectedInquiry: Inquiry | null;
  setSelectedInquiry: (inquiryId: string) => void;
  files: File[];
  createInquiry: (
    inquiryData: Partial<Omit<Inquiry, "id" | "createdAt" | "updatedAt" | "accountId">>,
  ) => Promise<string>;
  updateInquiry: (
    inquiryId: string,
    updatedData: Partial<Omit<Inquiry, "id" | "createdAt" | "updatedAt">>,
  ) => void;
  deleteInquiryById: (inquiryId: string) => void;
  addFileToInquiry: (
    inquiryId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ) => Promise<string>;
}

const InquiryContext = createContext<InquiryContextProps | undefined>(
  undefined,
);

export const useInquiry = (): InquiryContextProps => {
  const context = useContext(InquiryContext);
  if (!context) {
    throw new Error("useInquiry must be used within a InquiryProvider");
  }
  return context;
};

export const InquiryProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { account } = useAccount();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<InquiryStatus>(InquiryStatus.PENDING);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: true,
  });
  const [error, setError] = useState<Errors>({});

  useEffect(() => {
    if (!user || !account?.id) {
      setLoading((prev) => ({ ...prev, list: false }));
      return;
    }
    setError((prev) => ({ ...prev, list: null }));
    const docRef = query(
      collection(db, "inquiries"),
      where("accountId", "==", account.id),
      where("status", "==", status),
      orderBy("createdAt", "desc"),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        const inquiriesData: Inquiry[] = [];
        docSnapshot.forEach((doc) => {
          inquiriesData.push({ id: doc.id, ...doc.data() } as Inquiry);
        });
        setInquiries(inquiriesData);
        setLoading((prev) => ({ ...prev, list: false }));
      },
      (err) => {
        console.error("Error fetching inquiries: ", err);
        setError((prev) => ({ ...prev, list: err }));
        setLoading((prev) => ({ ...prev, list: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  }, [account?.id, user, status]);

  useEffect(() => {
    if (selectedInquiry) {
      setSelectedInquiry(selectedInquiry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInquiry]);

  useEffect(() => {
    if (!selectedInquiry) return;
    setError((prev) => ({ ...prev, details: null }));
    const docRef = doc(collection(db, "inquiries"), selectedInquiry.id);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setSelectedInquiry((prev) => ({ ...prev, ...data }) as Inquiry);
          setLoading((prev) => ({ ...prev, details: false }));
        }
      },
      (error) => {
        console.error("Error fetching inquiry: ", error);
        setError((prev) => ({ ...prev, details: error }));
        setLoading((prev) => ({ ...prev, details: false }));
      },
    );
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInquiry?.id]);

  const setSelectedInquiryById = async (inquiryId: string) => {
    const inquiryData = await InquiryService.getInquiry(inquiryId);
    setSelectedInquiry(inquiryData);

    const files = await InquiryService.getInquiryFiles(inquiryId);
    setFiles(files);
  };

  const createInquiry = async (
    inquiryData: Partial<Omit<Inquiry, "id" | "createdAt" | "updatedAt" | "accountId">>,
  ): Promise<string> => {
    const newInquiryId = await InquiryService.createInquiry({
      ...inquiryData,
      accountId: account.id,
    });
    return newInquiryId;
  };

  const updateInquiry = async (
    inquiryId: string,
    updatedData: Partial<Omit<Inquiry, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await InquiryService.updateInquiry(inquiryId, updatedData);
    setSelectedInquiryById(inquiryId);
  };

  const deleteInquiryById = async (inquiryId: string) => {
    await InquiryService.deleteInquiry(inquiryId);
  };

  const addFileToInquiry = async (
    inquiryId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ): Promise<string> => {
    const newFileId = await InquiryService.addFileToInquiry(inquiryId, {
      ...fileData,
      createdBy: user!.uid,
    });
    setSelectedInquiryById(inquiryId);
    return newFileId;
  };

  return (
    <InquiryContext.Provider
      value={{
        loading,
        error,
        status,
        setStatus,
        inquiries,
        selectedInquiry,
        files,
        createInquiry,
        updateInquiry,
        deleteInquiryById,
        setSelectedInquiry: setSelectedInquiryById,
        addFileToInquiry,
      }}
    >
      {children}
    </InquiryContext.Provider>
  );
};
